import { RotationRow, VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';
import { VehicleRowVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

import { Lane } from './types';

/**
 * バーも未計画の回転も持たないレーンの代わりの行。
 *
 * Why not レーンごと落とす: 左カラムの行数が中央のレーン数より減り、そこから下の行が
 * 1 本ずつ上へ詰まる。以降の全行が「別の車両の運行」の横に並ぶので、レーンを落とすより
 * 中身の無い行を 1 つ描くほうが害が小さい。
 *
 * `isPlaceholderRow` を立てるのは、実在する delivery に対応しない合成行だから
 * （選択・集計の対象から外す既存の扱いに合わせる）。
 */
function placeholderRow(lane: Lane): RotationRow {
  const source: VehicleRowVM = {
    deliveryId: -1,
    truckId: lane.truckId,
    driverId: lane.driverId,
    rotationNumber: 1,
    totalRotations: 1,
    driverColor: '',
    truckColor: null,
    totalItemCount: 0,
    cards: [],
    isPlaceholderRow: true,
  };
  return {
    rowId: `long-haul-lane-${lane.laneId}`,
    vehicleId: lane.vehicle.id,
    truckId: lane.truckId,
    driverId: lane.driverId,
    shiftTruckId: null,
    rotationNumber: 1,
    totalRotations: 1,
    cards: [],
    source,
    deliveryCards: [],
  };
}

/** そのレーンを代表する行。バーの元になった行 → 未計画の回転 → 合成行 の順に引く */
function representativeRow(lane: Lane): RotationRow {
  return lane.trips[0]?.source ?? lane.unplannedRows[0] ?? placeholderRow(lane);
}

/**
 * レーン → 既存の車両リスト（`ConvenienceDispatchVehicleList`）が受け取るグループ。
 *
 * あちらはグループ（= 勤務）ごとに回転行を積むが、長距離のレーンは `(truckId, driverId)`
 * 単位で、1 レーン = 1 行にしたい。ここで 1 レーン = 1 グループ = 1 行に畳む。
 * レーンは落とさない（行数がレーン数とずれると縦位置が合わなくなる。`placeholderRow` 参照）。
 */
export function buildLaneRowGroups(lanes: readonly Lane[]): VehicleRowGroup[] {
  return lanes.map((lane) => {
    const row = representativeRow(lane);
    return {
      vehicle: {
        ...lane.vehicle,
        // ドライバー未割当の勤務は車両ごとに 1 レーンへ束ねており、代表車両の側には
        // マスタの既定ドライバー名が入っていることがある。そのまま渡すと
        // 「ドライバー未設定」であるべき行に実在する人の名前が出る
        driverName: lane.driverId == null ? '' : lane.vehicle.driverName,
      },
      rows: [row],
      /**
       * 車両・車番・ドライバーの差し替え先。行ではなくグループから渡る値なので
       * （`VehicleListGroup` の `groupShiftTruckId`）、ここに入れないと 3 つとも開かない。
       *
       * レーンは期間内の勤務を時系列に並べたもので、複数日に渡ると勤務も複数ある。
       * 差し替えが効くのは代表行（最初の運行）の勤務 1 件だけ。
       */
      shiftTruckId: row.shiftTruckId ?? null,
      /**
       * グループの識別子。既定は shiftTruckId → vehicle.id の順だが、同じ勤務が時間の
       * 重なりで 2 レーンに割れることがあり、同じトラックに 2 人が乗ると vehicle.id も
       * 重複する。レーン id で一意にする。
       *
       * Why not vehicle.id を書き換える: `buildVehicleSortableGroups` が vehicle.id から
       * 並べ替えの単位 (truckId) を引くので、書き換えるとハンドルが掴めなくなる。
       */
      groupKey: `long-haul-lane-${lane.laneId}`,
    };
  });
}
